import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { SCREENSHOTS } from "../data/curated";

// Stable reference — recreating this array on every render would restart the
// entrance zoom each time the screenshot cycle advances mid-zoom (the "flash").
const ENTRANCE_ZOOM = [1.35, 1];

export default function PhoneMockup({ active, fast }) {
    const [idx, setIdx] = useState(0);
    const [prev, setPrev] = useState(null);
    const [flying, setFlying] = useState(false);

    const DOT_SLOT = 20; // px per dot slot in the filmstrip
    const DOT_VIEWPORT = 140; // 7 slots visible; the active one stays centred

    // Decode every screenshot up front so a switch never hitches on first paint
    useEffect(() => {
        SCREENSHOTS.forEach((src) => {
            const img = new Image();
            img.src = src;
            img.decode?.().catch(() => {});
        });
    }, []);

    useEffect(() => {
        if (active && !fast) setFlying(true);
    }, [active, fast]);

    useEffect(() => {
        if (!flying) return;
        const t = setTimeout(() => setFlying(false), 2200);
        return () => clearTimeout(t);
    }, [flying]);

    useEffect(() => {
        if (!active) return;
        // Screen 1 holds briefly while the one-time entrance zoom settles;
        // every other advance (including wrapping back to screen 1) is a
        // normal 3.5s hold with a plain crossfade — no zoom replay.
        const t = setTimeout(() => {
            setPrev(idx);
            const next = (idx + 1) % SCREENSHOTS.length;
            setIdx(next);
        }, idx === 0 && flying ? 1400 : 3500);
        return () => clearTimeout(t);
    }, [idx, active, flying]);

    const go = (i) => {
        setPrev(idx);
        setIdx(i);
    };

    // Mobile swipe: left = next screen, right = previous (pointer events, guarded;
    // mouse ignored so desktop stays click-only)
    const pointerStart = useRef(null);
    const onPointerDown = (e) => {
        if (e.pointerType === "mouse") return;
        pointerStart.current = { x: e.clientX, y: e.clientY };
    };
    const onPointerUp = (e) => {
        if (!pointerStart.current) return;
        const dx = e.clientX - pointerStart.current.x;
        const dy = e.clientY - pointerStart.current.y;
        pointerStart.current = null;
        if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.5) {
            setPrev(idx);
            setIdx((i) =>
                dx < 0
                    ? (i + 1) % SCREENSHOTS.length
                    : (i - 1 + SCREENSHOTS.length) % SCREENSHOTS.length
            );
        }
    };

    return (
        <motion.div
            initial={fast ? false : { opacity: 0, y: 60, rotate: 4 }}
            animate={active ? { opacity: 1, y: 0, rotate: 0 } : {}}
            transition={{
                duration: 1.2,
                ease: [0.16, 1, 0.3, 1],
                delay: 0.6,
            }}
            className="relative max-w-full"
            style={{ touchAction: "pan-y" }}
            onPointerDown={onPointerDown}
            onPointerUp={onPointerUp}
            data-testid="phone-mockup"
        >
            <motion.div
                className="relative"
                data-testid="phone-zoom"
                animate={{ scale: flying ? ENTRANCE_ZOOM : 1 }}
                transition={{ duration: 2.2, ease: [0.16, 1, 0.3, 1] }}
            >
                <div className="pointer-events-none absolute inset-0 -z-10 scale-[1.4] rounded-[5rem] bg-[radial-gradient(ellipse_at_center,rgba(3,135,226,0.65),transparent_68%)] blur-3xl" />
                <div
                    className="relative mx-auto w-[290px] max-w-full rounded-[3rem] border border-white/10 bg-coal p-2.5 shadow-[0_50px_120px_-30px_rgba(0,0,0,0.9),0_0_80px_-20px_rgba(3,135,226,0.25)] lg:w-[310px] 2xl:w-[350px]"
                    style={{ maxWidth: "100%" }}
                >
                    <div className="relative aspect-[9/19] overflow-hidden rounded-[2.4rem] bg-void">
                        {SCREENSHOTS.map((src, i) => (
                            <div
                                key={src}
                                className={`absolute inset-0 ${
                                    i === idx ? "z-[2]" : i === prev ? "z-[1]" : "z-0"
                                }`}
                                style={{
                                    transform: "translateZ(0)",
                                    willChange: "transform",
                                }}
                            >
                                <img
                                    src={src}
                                    alt={`RIVVR app screen ${i + 1}`}
                                    data-testid={`phone-screen-${i}`}
                                    className={`h-full w-full object-cover ${
                                        i === idx || i === prev
                                            ? "opacity-100"
                                            : "opacity-0"
                                    }`}
                                    style={{
                                        transition:
                                            "opacity 450ms ease-in-out",
                                    }}
                                />
                            </div>
                        ))}
                        <div className="absolute left-1/2 top-3 z-10 flex h-[20px] w-[82px] -translate-x-1/2 items-center justify-end rounded-full bg-black pr-2">
                            <span className="h-[8px] w-[8px] rounded-full bg-[#0a0f18]" />
                        </div>
                    </div>
                </div>
            </motion.div>
            <div className="mt-6 -translate-y-[16px] flex justify-center">
                <div
                    className="overflow-hidden"
                    style={{ width: DOT_VIEWPORT }}
                    data-testid="phone-dots-viewport"
                >
                    <div
                        className="flex items-center"
                        style={{
                            transform: `translateX(${(DOT_VIEWPORT / 2 - DOT_SLOT / 2) - idx * DOT_SLOT}px)`,
                            transition: "transform 450ms ease-in-out",
                            willChange: "transform",
                        }}
                        data-testid="phone-dots-track"
                    >
                        {SCREENSHOTS.map((_, i) => (
                            <button
                                key={i}
                                data-testid={`phone-dot-${i}`}
                                aria-label={`Show screen ${i + 1}`}
                                onClick={() => go(i)}
                                className="flex h-4 shrink-0 items-center justify-center"
                                style={{ width: DOT_SLOT }}
                            >
                                <span
                                    className={`h-1.5 rounded-full transition-all duration-500 ${
                                        i === idx
                                            ? "w-5 bg-neon"
                                            : "w-1.5 bg-white/20 hover:bg-white/40"
                                    }`}
                                />
                            </button>
                        ))}
                    </div>
                </div>
            </div>
        </motion.div>
    );
}
